import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';

const IsoServices: React.FC = () => {
  const isoStandards = [
    {
      standard: "ISO 9001:2015",
      name: "Quality Management System",
      description: "Focuses on meeting customer requirements and enhancing customer satisfaction through effective quality management.",
    },
    {
      standard: "ISO 27001:2022",
      name: "Information Security Management",
      description: "Addresses the management of information security risks and helps protect sensitive information assets.",
    },
    {
      standard: "ISO 42001:2023",
      name: "AI Management System",
      description: "Provides a management system framework for responsible AI: governance, risk management, and continual improvement across AI lifecycle.",
    },
    {
      standard: "ISO 20000-1:2018",
      name: "IT Service Management",
      description: "Specifies requirements for establishing, implementing, maintaining and improving an IT service management system.",
    },
    {
      standard: "ISO 22301:2019",
      name: "Business Continuity Management",
      description: "Provides a framework for establishing, implementing, maintaining, and improving business continuity management systems.",
    },
    {
      standard: "ISO 45001:2018",
      name: "Occupational Health & Safety",
      description: "Helps organizations improve worker safety, reduce workplace risks, and create better, safer working conditions.",
    },
    {
      standard: "ISO 31000:2018",
      name: "Risk Management",
      description: "Provides principles, framework, and a process for managing risk that can be used by any organization.",
    }
  ];

  const isoServices = [
    {
      title: "ISO 9001:2015 Services",
      description: "Our comprehensive ISO 9001 services help organizations implement and maintain effective quality management systems that meet customer requirements and enhance satisfaction.",
      benefits: [
        "Improved quality of products and services",
        "Enhanced customer satisfaction",
        "Streamlined processes and operations",
        "Consistent management approach"
      ],
      path: "/services/iso/iso9001"
    },
    {
      title: "ISO 27001:2022 Services",
      description: "Our ISO 27001 services help organizations establish and maintain an effective information security management system to safeguard sensitive information assets.",
      benefits: [
        "Robust information security framework",
        "Protection against security threats",
        "Compliance with regulatory requirements",
        "Enhanced stakeholder confidence"
      ],
      path: "/services/iso/iso27001"
    },
    {
      title: "ISO 42001:2023 Services",
      description: "Our ISO 42001 services help organizations establish an AI management system to govern AI responsibly, manage AI risks, and demonstrate trustworthy AI practices.",
      benefits: [
        "Stronger AI governance and accountability",
        "Improved AI risk management across the lifecycle",
        "Better alignment with regulatory and stakeholder expectations",
        "Increased trust in AI-enabled products and services"
      ],
      path: "/services/iso/iso42001"
    },
    {
      title: "ISO 20000-1:2018 Services",
      description: "Our ISO 20000-1 services help organizations establish, implement, maintain and continually improve an IT service management system.",
      benefits: [
        "Improved IT service delivery",
        "Enhanced customer satisfaction",
        "Better resource management",
        "Streamlined service operations"
      ],
      path: "/services/iso/iso20000"
    },
    {
      title: "ISO 22301:2019 Services",
      description: "Our ISO 22301 services help organizations establish, implement, maintain, and improve business continuity management systems to protect against disruptive incidents.",
      benefits: [
        "Enhanced organizational resilience",
        "Reduced downtime and financial losses",
        "Protection of critical business functions",
        "Improved stakeholder confidence"
      ],
      path: "/services/iso/iso22301"
    },
    {
      title: "ISO 45001:2018 Services",
      description: "Our ISO 45001 services help organizations improve worker safety, reduce workplace risks, and create better, safer working conditions.",
      benefits: [
        "Reduced workplace accidents",
        "Improved worker health and safety",
        "Compliance with legal requirements",
        "Enhanced organizational reputation"
      ],
      path: "/services/iso/iso45001"
    },
    {
      title: "ISO 31000:2018 Services",
      description: "Our ISO 31000 services help organizations implement effective risk management frameworks to enhance decision-making and organizational resilience.",
      benefits: [
        "Improved decision-making",
        "Better risk identification and assessment",
        "Enhanced organizational resilience",
        "Compliance with governance requirements"
      ],
      path: "/services/iso/iso31000"
    },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              ISO Services
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Comprehensive ISO consultancy and certification services to meet international standards
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Introduction Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', margin: '-24px' }}>
          <div style={{ width: '50%', padding: '24px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                What are ISO Standards?
              </Typography>
              <Typography paragraph>
                ISO (International Organization for Standardization) standards are internationally recognized 
                guidelines and specifications for products, services, and systems to ensure quality, safety, and efficiency.
                These standards help organizations improve their performance while ensuring compliance with industry best practices.
              </Typography>
              <Typography paragraph>
                ISO standards are developed through a consensus process and are adopted by organizations worldwide 
                to demonstrate their commitment to excellence, consistency, and continuous improvement in their operations.
              </Typography>
            </motion.div>
          </div>
          <div style={{ width: '50%', padding: '24px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="ISO Standards"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </div>
        </div>
      </Container>

      {/* ISO Standards Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 6 }}
            >
              ISO Standards We Support
            </Typography>
          </motion.div>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', margin: '-12px' }}>
            {isoStandards.map((standard, index) => (
              <div key={index} style={{ width: '33.33%', padding: '12px', minWidth: '250px' }}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      bgcolor: '#1976d2',
                    }} />
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      fontWeight="bold"
                      color="primary"
                    >
                      {standard.standard}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      fontWeight="medium"
                    >
                      {standard.name}
                    </Typography>
                    <Typography color="text.secondary">
                      {standard.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </div>
            ))}
          </div>
        </Container>
      </Box>

      {/* Our ISO Services Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
            sx={{ mb: 6 }}
          >
            Our ISO Services
          </Typography>
        </motion.div>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', margin: '-20px' }}>
          {isoServices.map((service, index) => (
            <div key={index} style={{ width: '33.33%', padding: '20px', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 5,
                    height: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    backgroundColor: '#1976d215',
                    color: '#1976d2',
                    mb: 3,
                  }}>
                    <VerifiedUserIcon sx={{ fontSize: 40 }} />
                  </Box>
                  
                  <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom color="#0c2340">
                    {service.title}
                  </Typography>
                  
                  <Typography paragraph color="text.secondary" sx={{ mb: 3 }}>
                    {service.description}
                  </Typography>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="h6" gutterBottom fontWeight="medium">
                    Key Benefits:
                  </Typography>
                  
                  <List sx={{ mb: 3 }}>
                    {service.benefits.map((benefit, idx) => (
                      <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleOutlineIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={benefit} />
                      </ListItem>
                    ))}
                  </List>
                  
                  <Box sx={{ mt: 'auto' }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      endIcon={<ArrowForwardIcon />}
                      component={Link}
                      to={service.path}
                      sx={{
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: '#1976d215',
                        },
                      }}
                    >
                      Learn More
                    </Button>
                  </Box>
                </Paper>
              </motion.div>
            </div>
          ))}
        </div>
      </Container>

      {/* Our Approach Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Typography 
                  variant="h4" 
                  component="h2" 
                  gutterBottom 
                  fontWeight="bold"
                  color="#0c2340"
                >
                  Our Approach to ISO Certification
                </Typography>
                <Typography paragraph>
                  At Right Track Technologies Limited, we follow a systematic approach to help organizations achieve 
                  ISO certification. Our team of experienced consultants works closely with your organization 
                  to understand your specific needs and develop a tailored implementation plan.
                </Typography>
                
                <List>
                  {[
                    "Gap Analysis: We assess your current processes against ISO requirements to identify areas for improvement.",
                    "Implementation Planning: We develop a roadmap for implementing the necessary changes to meet ISO standards.",
                    "Documentation: We help you develop and maintain the required documentation for ISO certification.",
                    "Training: We provide training to your staff on ISO requirements and best practices.",
                    "Internal Audits: We conduct internal audits to ensure compliance with ISO standards before formal certification.",
                    "Certification Support: We provide support during the formal certification audit to ensure a successful outcome."
                  ].map((step, index) => (
                    <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={step} />
                    </ListItem>
                  ))}
                </List>
              </motion.div>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Box 
                  component="img"
                  src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                  alt="ISO Certification Process"
                  sx={{
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  }}
                />
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ 
        backgroundColor: '#fff', 
        py: 12,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                position: 'relative'
              }}
            >
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Ready to Achieve ISO Certification?
              </Typography>
              <Typography 
                variant="body1" 
                paragraph 
                sx={{ 
                  maxWidth: '700px', 
                  mx: 'auto', 
                  mb: 4, 
                  opacity: 0.9 
                }}
              >
                Our ISO experts are ready to help you implement effective management systems and achieve certification.
                Contact us today for a free consultation.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/contact?service=iso"
                  sx={{
                    bgcolor: 'white',
                    color: '#0c2340',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px',
                    fontWeight: 'bold'
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/services"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px'
                  }}
                >
                  View All Services
                </Button>
              </Box>

              {/* Decorative circles */}
              <Box sx={{
                position: 'absolute',
                top: -30,
                left: -30,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
              <Box sx={{
                position: 'absolute',
                bottom: -40,
                right: -40,
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default IsoServices; 